import React, { useState } from "react";
import { getImageUrl } from "../../../utils/getImageUrl";
import { scrollToId } from "../../../utils/comptiaCerts";

// A single checklist row used inside the Overview / Career Path tab content.
function CheckItem({ children }) {
  return (
    <li className="flex items-start gap-3">
      <svg className="w-5 h-5 text-custom-pink flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-gray-800 leading-relaxed">{children}</span>
    </li>
  );
}

// One certification: title/description/badge header, and a tab bar that
// varies per certification — Overview is always shown; Career Path, Mode,
// Duration, and Fee each only appear when the CMS has that tab switched on.
export default function CompTIACertCard({ cert, visible = true, delay = 0, domId }) {
  const [tab, setTab] = useState("overview");
  const skills = Array.isArray(cert.skills) ? cert.skills : [];
  // Normalize: older data may have stored careerPath as a flat string[].
  const careerPath = (Array.isArray(cert.careerPath) ? cert.careerPath : []).map((role) =>
    typeof role === "string" ? { title: role, description: "" } : role
  );

  // Certs saved before this toggle existed were always showing Career Path — default true.
  const showCareerPath = cert.showCareerPath !== false;
  const showMode = cert.showMode === true;
  const showDuration = cert.showDuration === true;
  const showFee = cert.showFee === true;

  const TABS = [
    { key: "overview", label: "Overview" },
    ...(showCareerPath ? [{ key: "career", label: "Career Path" }] : []),
    ...(showMode ? [{ key: "mode", label: "Mode" }] : []),
    ...(showDuration ? [{ key: "duration", label: "Duration" }] : []),
    ...(showFee ? [{ key: "fee", label: "Fee" }] : []),
  ];

  return (
    <div
      id={domId}
      className={`bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden transform transition-all duration-700 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Header: name + description + badge */}
      <div className="p-8 md:p-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
        <div className="flex-1">
          <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">{cert.name}</h3>
          {cert.body && cert.body.split(/\n\n+/).filter(Boolean).map((p, i) => (
            <p key={i} className="text-base md:text-lg text-gray-700 leading-relaxed max-w-2xl mb-3 last:mb-0 whitespace-pre-wrap">{p}</p>
          ))}
        </div>
        {cert.badge_image && (
          <img
            src={getImageUrl(cert.badge_image)}
            alt={cert.name}
            className="w-24 h-24 md:w-28 md:h-28 object-contain flex-shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
      </div>

      {/* Tab bar — Overview / Career Path only */}
      <div className="flex bg-gray-900">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-8 py-4 text-sm md:text-base font-semibold tracking-wide transition-colors border-b-4 ${
              tab === t.key
                ? "text-white border-custom-pink"
                : "text-gray-400 border-transparent hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-8 md:p-10 bg-gray-50">
        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Skills you&apos;ll learn</h4>
              <p className="text-sm text-gray-500 mb-5">
                Build skills with CompTIA learning and validate them with {cert.name} certification.
              </p>
              {skills.length > 0 ? (
                <ul className="space-y-3">
                  {skills.map((s, i) => <CheckItem key={i}>{s}</CheckItem>)}
                </ul>
              ) : (
                <p className="text-gray-500 italic">Skills content coming soon.</p>
              )}
            </div>

            {/* Sidebar CTA */}
            <div>
              <div className="rounded-2xl p-6 text-white bg-gradient-to-br from-custom-pink to-gold shadow-lg">
                <h5 className="text-lg font-bold mb-2">Ready to get certified?</h5>
                <p className="text-sm text-white/90 mb-5 leading-relaxed">
                  Start your {cert.name} journey with JRIIT — an authorised CompTIA training partner in Tanzania.
                </p>
                <button
                  type="button"
                  onClick={() => scrollToId("application-form")}
                  className="w-full bg-white text-custom-pink font-bold py-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "career" && (
          <div>
            {(cert.careerPathTitle || cert.careerPathIntro) && (
              <div className="mb-8">
                {cert.careerPathTitle && (
                  <h4 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">{cert.careerPathTitle}</h4>
                )}
                {cert.careerPathIntro && cert.careerPathIntro.split(/\n\n+/).filter(Boolean).map((p, i) => (
                  <p key={i} className="text-base text-gray-700 leading-relaxed mb-3 last:mb-0 whitespace-pre-wrap">{p}</p>
                ))}
              </div>
            )}

            {cert.careerPathImage ? (
              <img
                src={getImageUrl(cert.careerPathImage)}
                alt={`${cert.name} career path diagram`}
                className="w-full max-w-3xl mx-auto rounded-2xl object-contain mb-10"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="mb-10 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center py-14 px-6 text-gray-400">
                <p className="text-sm text-center max-w-xs">Career Path Diagram — upload via dashboard</p>
              </div>
            )}

            {careerPath.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {careerPath.map((role, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-2xl p-6">
                    <p className="font-bold text-gray-900 text-lg mb-2">{role.title}</p>
                    {role.description && (
                      <p className="text-sm text-gray-700 leading-relaxed">{role.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic text-center">Career path content coming soon.</p>
            )}
          </div>
        )}

        {tab === "mode" && (
          <div className="max-w-xl mx-auto text-center py-6">
            <p className="text-sm text-gray-500 uppercase tracking-widest mb-3">Delivery Mode</p>
            {cert.mode ? (
              <p className="text-3xl md:text-4xl font-extrabold text-custom-pink">{cert.mode}</p>
            ) : (
              <p className="text-gray-500 italic">Mode details coming soon.</p>
            )}
          </div>
        )}

        {tab === "duration" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto py-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
              <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Duration</p>
              <p className="text-2xl md:text-3xl font-extrabold text-gray-900">{cert.duration || "Coming soon"}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
              <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Study Hours</p>
              <p className="text-2xl md:text-3xl font-extrabold text-gray-900">{cert.hours || "Coming soon"}</p>
            </div>
          </div>
        )}

        {tab === "fee" && (
          <div className="max-w-xl mx-auto text-center py-6">
            <p className="text-sm text-gray-500 uppercase tracking-widest mb-3">Course Fee</p>
            {cert.fee ? (
              <p className="text-3xl md:text-4xl font-extrabold text-custom-pink mb-6">{cert.fee}</p>
            ) : (
              <p className="text-gray-500 italic mb-6">Fee details coming soon.</p>
            )}
            <button
              type="button"
              onClick={() => scrollToId("application-form")}
              className="bg-custom-pink text-white font-bold px-8 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              Apply Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
